// ...existing code...
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Alert,
  Autocomplete,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  Save,
  AttachMoney
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import inventoryService from '../../services/inventoryService'; // <-- fix ESLint no-undef

const MotionDiv = motion.div;

const ProductionForm = ({
  open,
  onClose,
  onSave,
  initialData = null,
  products = [],
  loading = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    date: new Date(),
    product_id: '',
    quantity_produced: '',
    unit_cost: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [recipe, setRecipe] = useState(null);

  useEffect(() => {
    let mounted = true;
    const loadRecipe = async () => {
      if (!selectedProduct?.item_id) return setRecipe(null);
      try {
        const res = await inventoryService.getRecipeByItem(selectedProduct.item_id);
        if (!mounted) return;
        setRecipe(res.data || null);
      } catch (err) {
        console.warn('No recipe found or error fetching recipe', err);
        setRecipe(null);
      }
    };
    loadRecipe();
    return () => { mounted = false; };
  }, [selectedProduct]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        date: new Date(initialData.date),
        product_id: initialData.product_id || '',
        quantity_produced: initialData.quantity_produced?.toString() || '',
        unit_cost: initialData.unit_cost?.toString() || '',
        notes: initialData.notes || ''
      });
      const product = products.find(p => p.item_id === initialData.product_id);
      setSelectedProduct(product || null);
    } else {
      setFormData({
        date: new Date(),
        product_id: '',
        quantity_produced: '',
        unit_cost: '',
        notes: ''
      });
      setSelectedProduct(null);
    }
    setErrors({});
  }, [initialData, products, open]);

  const totalCost = parseFloat(formData.quantity_produced || 0) * parseFloat(formData.unit_cost || 0);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleProductChange = (event, newValue) => {
    setSelectedProduct(newValue);
    handleInputChange('product_id', newValue?.item_id || '');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.product_id) newErrors.product_id = t('inventory.validation.productRequired') || 'Product is required';
    if (!formData.quantity_produced || parseFloat(formData.quantity_produced) <= 0) newErrors.quantity_produced = t('inventory.validation.quantityRequired') || 'Valid quantity is required';
    if (!formData.unit_cost || parseFloat(formData.unit_cost) < 0) newErrors.unit_cost = t('inventory.validation.unitCostRequired') || 'Valid unit cost is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Build ingredient preview and totals
  const ingredientPreview = (recipe && formData.quantity_produced)
    ? recipe.ingredients.map(i => {
        const qty_used = (parseFloat(i.quantity_per_yield || 0) * (parseFloat(formData.quantity_produced || 0) / (parseFloat(recipe.yield_quantity || 1))));
        const unit = i.unit || 'pcs';
        // cost_per_unit expected to be cost per unit in DB (same unit as recipe quantity)
        const cost = (parseFloat(i.cost_per_unit || 0) * qty_used);
        return { ...i, qty_used: parseFloat(qty_used.toFixed(6)), cost: parseFloat(cost.toFixed(4)), unit };
      })
    : [];

  const totalIngredientCost = ingredientPreview.reduce((s, it) => s + (it.cost || 0), 0);

  const handleSubmit = () => {
    if (!validateForm()) return;
    const submitData = {
      date: format(formData.date, 'yyyy-MM-dd'),
      product_id: formData.product_id,
      quantity_produced: parseFloat(formData.quantity_produced),
      unit_cost: parseFloat(formData.unit_cost),
      total_cost: parseFloat(totalCost.toFixed(2)),
      notes: formData.notes.trim() || null
    };
    onSave(submitData);
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile} disableEscapeKeyDown={loading}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
          <TrendingUp />
          {initialData ? (t('inventory.productionLog.editRecord') || 'Edit Production Record') : (t('inventory.productionLog.addRecord') || 'Add Production Record')}
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <MotionDiv initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <DatePicker label={t('inventory.date') || 'Date'} value={formData.date} onChange={(v) => handleInputChange('date', v)} disabled={loading}
                  slotProps={{ textField: { fullWidth: true, error: !!errors.date, helperText: errors.date } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete value={selectedProduct} onChange={handleProductChange} options={products} getOptionLabel={(o) => o.name || ''}
                  disabled={loading}
                  renderInput={(params) => <TextField {...params} label={t('inventory.product') || 'Product'} error={!!errors.product_id} helperText={errors.product_id} required />}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', gap: 2, py: 1 }}>
                      {option.image ? <Avatar src={option.image} /> : <Avatar>{option.name?.charAt(0) || '?'}</Avatar>}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.category_name} • Current: {option.current_quantity || 0}</Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              {selectedProduct && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'success.lighter' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>{t('inventory.selectedProduct') || 'Selected Product'}</Typography>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                      {selectedProduct.image ? <Avatar src={selectedProduct.image} sx={{ width: 40, height: 40 }} /> : <Avatar sx={{ width: 40, height: 40 }}>{selectedProduct.name?.charAt(0)}</Avatar>}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedProduct.name}</Typography>
                        <Typography variant="caption" color="text.secondary">Current Stock: {selectedProduct.current_quantity || 0} {selectedProduct.unit_of_measure || 'pcs'}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              )}

              {selectedProduct && formData.quantity_produced && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'info.lighter' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>📈 Stock Impact Preview</Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Typography>Current: <strong>{selectedProduct.current_quantity || 0}</strong></Typography>
                      <Typography color="success.main">+ {formData.quantity_produced}</Typography>
                      <Typography>→ New: <strong>{(selectedProduct.current_quantity || 0) + parseInt(formData.quantity_produced || 0)}</strong></Typography>
                    </Stack>
                  </Paper>
                </Grid>
              )}

              {/* Ingredient usage preview */}
              {recipe && ingredientPreview.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: 'neutral.100' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>🧾 Ingredient Usage Preview</Typography>
                    <List dense>
                      {ingredientPreview.map((ing) => (
                        <React.Fragment key={ing.ingredient_item_id}>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{ing.ingredient_name || `Item ${ing.ingredient_item_id}`}</Typography>
                                  <Typography variant="body2" color="text.secondary">{ing.qty_used} {ing.unit}</Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">Unit cost: ${parseFloat(ing.cost_per_unit || 0).toFixed(4)}</Typography>
                                  <Typography variant="caption" color="text.secondary">Line cost: ${ing.cost.toFixed(4)}</Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          <Divider />
                        </React.Fragment>
                      ))}
                      <ListItem>
                        <ListItemText
                          primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>Total Ingredient Cost</Typography>}
                          secondary={<Typography variant="caption" color="text.secondary">${totalIngredientCost.toFixed(4)}</Typography>}
                        />
                      </ListItem>
                    </List>
                    {ingredientPreview.some(i => !i.cost_per_unit || i.cost_per_unit === 0) && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Some ingredients have no cost set (cost_per_unit = 0). Update Stock Setup for accurate production cost.
                      </Alert>
                    )}
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('inventory.quantityProduced') || 'Quantity Produced'} type="number"
                  value={formData.quantity_produced} onChange={(e) => handleInputChange('quantity_produced', e.target.value)}
                  error={!!errors.quantity_produced} helperText={errors.quantity_produced} disabled={loading} required inputProps={{ min: 0, step: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('inventory.unitCost') || 'Unit Cost'} type="number"
                  value={formData.unit_cost} onChange={(e) => handleInputChange('unit_cost', e.target.value)}
                  error={!!errors.unit_cost} helperText={errors.unit_cost} disabled={loading} required InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
              </Grid>

              {formData.quantity_produced && formData.unit_cost && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoney color="primary" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Total Cost: ${totalCost.toFixed(2)}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">{formData.quantity_produced} × ${parseFloat(formData.unit_cost || 0).toFixed(2)} = ${totalCost.toFixed(2)}</Typography>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField fullWidth label={t('inventory.notes') || 'Notes (Optional)'} multiline rows={3}
                  value={formData.notes} onChange={(e) => handleInputChange('notes', e.target.value)} disabled={loading} />
              </Grid>

              {Object.keys(errors).length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="error">{t('inventory.validation.pleaseFixErrors') || 'Please fix the errors above before submitting.'}</Alert>
                </Grid>
              )}
            </Grid>
          </MotionDiv>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={loading}> {t('common.cancel') || 'Cancel'} </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading || Object.keys(errors).length > 0} startIcon={<Save />}>
            {loading ? (t('common.saving') || 'Saving...') : (initialData ? (t('common.update') || 'Update') : (t('common.save') || 'Save'))}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ProductionForm;